/** Shared bits of the Site Builder store pages: country select, product/collection pickers, money input. */
import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Empty, Input, InputNumber, List, Modal, Select, Space, Spin, Typography } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import CountryFlag, { countryName } from '../../../shared/components/CountryFlag';
import { currencyDigits, fromMinor, listOf, money, toMinor, toastErr, useSite } from './common';

const { Text } = Typography;

export const COUNTRY_CODES = ('AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW').split(' ');

/** Select of ISO countries (flag + localized name). allowRest adds "*" = rest of world. */
export const CountrySelect = ({ allowRest = false, ...rest }) => {
    const { t } = useTranslation();
    const options = useMemo(() => {
        const list = COUNTRY_CODES.map((c) => ({ value: c, name: countryName(c) || c }))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((c) => ({ value: c.value, search: `${c.value} ${c.name}`.toLowerCase(), label: <Space size={6}><CountryFlag code={c.value} />{c.name}</Space> }));
        return allowRest ? [{ value: '*', search: '* rest of world', label: t('Rest of world') }, ...list] : list;
    }, [allowRest, t]);
    return <Select showSearch options={options} filterOption={(input, o) => o.search.includes(input.toLowerCase())} {...rest} />;
};

export const countryLabel = (code, t) => (code === '*' ? t('Rest of world') : (countryName(code) || code));

/** Money input in major units; value/onChange in minor units (shop currency). */
export const MoneyInput = ({ value, onChange, currency, ...rest }) => {
    const d = currencyDigits(currency);
    return (
        <InputNumber
            min={0}
            step={d ? 0.01 : 1}
            precision={d}
            value={fromMinor(value, currency)}
            onChange={(v) => onChange?.(toMinor(v, currency))}
            addonBefore={currency}
            style={{ width: '100%' }}
            {...rest}
        />
    );
};

export const Thumb = ({ src, size = 40 }) => (
    <div style={{ width: size, height: size, borderRadius: 6, overflow: 'hidden', background: 'var(--ant-color-fill-tertiary, #f5f5f5)', display: 'grid', placeItems: 'center', flex: 'none' }}>
        {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <PictureOutlined style={{ color: '#bbb' }} />}
    </div>
);

/** Modal to pick products (multi). onOk(selectedProducts) */
export const ProductPicker = ({ open, onClose, onOk, initial = [], title }) => {
    const { t } = useTranslation();
    const { api, currency } = useSite();
    const [q, setQ] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sel, setSel] = useState({});

    useEffect(() => {
        if (open) setSel(Object.fromEntries((initial || []).map((p) => [p.id, p])));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    useEffect(() => {
        if (!open) return undefined;
        const h = setTimeout(async () => {
            setLoading(true);
            try {
                setRows(listOf(await api.get('/products', { q, per_page: 50 })));
            } catch (e) { toastErr(e); } finally { setLoading(false); }
        }, 250);
        return () => clearTimeout(h);
    }, [open, q, api]);

    const toggle = (p) => setSel((s) => { const n = { ...s }; if (n[p.id]) delete n[p.id]; else n[p.id] = p; return n; });
    return (
        <Modal open={open} onCancel={onClose} title={title || t('Select products')} onOk={() => onOk(Object.values(sel))} okText={t('Done')} destroyOnHidden width={560}>
            <Input.Search allowClear placeholder={t('Search products')} value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 12 }} />
            <Spin spinning={loading}>
                <div style={{ maxHeight: 420, overflow: 'auto' }}>
                    {rows.length ? (
                        <List dataSource={rows} renderItem={(p) => (
                            <List.Item onClick={() => toggle(p)} style={{ cursor: 'pointer', padding: '6px 4px' }}>
                                <Space>
                                    <Checkbox checked={!!sel[p.id]} />
                                    <Thumb src={p.image?.src} size={36} />
                                    <div>
                                        <div>{p.title}</div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{money(p.price_min, currency)}</Text>
                                    </div>
                                </Space>
                            </List.Item>
                        )} />
                    ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                </div>
            </Spin>
            <Text type="secondary">{t('{{n}} selected', { n: Object.keys(sel).length })}</Text>
        </Modal>
    );
};

/** Load all collections of the site once (for selects). */
export const useCollections = () => {
    const { api } = useSite();
    const [list, setList] = useState([]);
    useEffect(() => {
        let live = true;
        api.get('/collections', null, { skipGlobalErrorToast: true }).then((d) => live && setList(listOf(d))).catch(() => {});
        return () => { live = false; };
    }, [api]);
    return list;
};

/** native HTML5 drag-and-drop reorder helper: returns props for an item at index i */
export const useDragList = (items, setItems) => {
    const [drag, setDrag] = useState(null);
    const props = (i) => ({
        draggable: true,
        onDragStart: (e) => { setDrag(i); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch (_) { /* ignore */ } },
        onDragOver: (e) => { if (drag !== null) e.preventDefault(); },
        onDrop: (e) => {
            e.preventDefault();
            if (drag === null || drag === i) return;
            const next = [...items];
            const [m] = next.splice(drag, 1);
            next.splice(i, 0, m);
            setItems(next);
            setDrag(null);
        },
        onDragEnd: () => setDrag(null),
        style: { opacity: drag === i ? 0.4 : 1, cursor: 'grab' },
    });
    return props;
};
